import { Controller } from "@hotwired/stimulus"
import { get, post, put, patch, destroy } from '@rails/request.js'

export default class extends Controller {
  static targets = [ "editor", "wrapper", "input", "urlTextInput", "urlNewTabInput", "urlInput", "imgInput", "imagePicker", "progressBar", "imgUrlInput", "imgPreview", "imgAltInput", "imgTitleInput", "imagePickerContainer", "imageDetailContainer", "imageSubmitButton" ]
  static classes = [ "focus" ]

  async connect() {
    const { Editor } = await import('@tiptap/core')
    const StarterKit = (await import('@tiptap/starter-kit')).default
    const Link = (await import('@tiptap/extension-link')).default
    const Image = (await import('@tiptap/extension-image')).default
    const TextAlign = (await import('@tiptap/extension-text-align')).default

    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            rel: null,
            target: null,
          }
        }),
        Image,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        })
      ],
      content: this.inputTarget.value,
    })

    this.editor.on('focus', ({ editor, event }) => {
      this.wrapperTarget.classList.add(this.focusClass)
    })

    this.editor.on('blur', ({ editor, event }) => {
      this.wrapperTarget.classList.remove(this.focusClass)
    })

    this.editor.on('update', ({ editor, event }) => {
      this.inputTarget.value = this.editor.getHTML()
      this.inputTarget.dispatchEvent(new Event("input"))

    })
  }

  async startUppy() {
    const Uppy = (await import('@uppy/core')).default
    const XHRUpload = (await import('@uppy/xhr-upload')).default
    const DragDrop = (await import('@uppy/drag-drop')).default
    const ProgressBar = (await import('@uppy/progress-bar')).default

    this.uppy = new Uppy({ autoProceed: true, restrictions: { maxNumberOfFiles: 1 } })
          .use(
            DragDrop, { target: this.imagePickerTarget }
          )
          .use(
            XHRUpload, { endpoint: '/upload/photos' }
          )
          .use(
            ProgressBar, { target: this.progressBarTarget, hideAfterFinish: false }
          )

    this.uppy.on('complete', (result) => {
      const filesAdded = result.successful.map((file) => file.response.body)

      this.addImage(filesAdded[0])
      this.uppy.reset()
    })
  }

  imagePickerTargetDisconnected() {
    if (this.uppy) {
      this.uppy.close()
    }
  }

  imagePickerTargetConnected() {
    this.startUppy()
  }

  async addImage(image) {
    const res = await post(`/admin/photos`, {
      body: {
        file: image
      },
      responseKind: 'json'
    })

    if (res.ok) {
      const result = await res.json
      console.log(result.photo_url)

      this.imgUrlInputTarget.value = result.photo_url
      this.imagePickerContainerTarget.classList.add('hidden')
      this.imageDetailContainerTarget.classList.remove('hidden')
      this.imageSubmitButtonTarget.classList.remove('hidden')
      this.imgPreviewTarget.src = result.photo_url
    }
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy()
    }
  }

  focus() {
    this.editor.chain().focus().run()
  }

  bold() {
    this.editor.chain().focus().toggleBold().run()
  }

  italic() {
    this.editor.chain().focus().toggleItalic().run()
  }

  strike() {
    this.editor.chain().focus().toggleStrike().run()
  }

  h1() {
    this.editor.chain().focus().toggleHeading({ level: 1 }).run()
  }

  h2() {
    this.editor.chain().focus().toggleHeading({ level: 2 }).run()
  }

  h3() {
    this.editor.chain().focus().toggleHeading({ level: 3 }).run()
  }

  h4() {
    this.editor.chain().focus().toggleHeading({ level: 4 }).run()
  }

  h5() {
    this.editor.chain().focus().toggleHeading({ level: 5 }).run()
  }

  h6() {
    this.editor.chain().focus().toggleHeading({ level: 6 }).run()
  }

  quote() {
    this.editor.chain().focus().toggleBlockquote().run()
  }

  textAlignLeft() {
    this.editor.chain().focus().setTextAlign('left').run()
  }

  textAlignCenter() {
    this.editor.chain().focus().setTextAlign('center').run()
  }

  textAlignRight() {
    this.editor.chain().focus().setTextAlign('right').run()
  }

  bulletList() {
    this.editor.chain().focus().toggleBulletList().run()
  }

  orderedList() {
    this.editor.chain().focus().toggleOrderedList().run()
  }

  unsetAllMarks() {
    this.editor.chain().focus().unsetAllMarks().run()
  }

  clearNodes() {
    this.editor.chain().focus().clearNodes().run()
  }

  getSelection() {
    const { view, state } = this.editor
    const { from, to } = view.state.selection
    return state.doc.textBetween(from, to, '')
  }

  showLinkModal() {
    if (this.editor.isActive('link')) {
      this.editor.commands.unsetLink()
    } else {
      if (this.getSelection()) {
        this.urlTextInputTarget.value = this.getSelection()
        this.urlNewTabInputTarget.checked = true
        // this.urlFollowInputTarget.checked = true
        this.urlInputTarget.value = ''
      }
      this.dispatch('showLinkModal', { target: document })
    }
  }

  showImageModal() {
    this.dispatch('showImageModal', { target: document })
  }

  urlInputKeydown(event) {
    if (event.key == "Enter") {
      event.preventDefault()
      this.setLink()
      this.dispatch('hideLinkModal', { target: document })
    }
  }

  setLink() {
    let target = '_blank'
    if (!this.urlNewTabInputTarget.checked) {
      target = null
    }

    // let rel = 'noopener noreferrer nofollow'
    // if (this.urlFollowInputTarget.checked) {
    //   rel = null
    // }

    this.editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: this.urlInputTarget.value, target: target })
        .command(({ tr }) => {
          tr.insertText(this.urlTextInputTarget.value)
          return true
        })
        .run()
  }

  setImage() {
    if (this.imgUrlInputTarget.value && this.imgUrlInputTarget.value.length > 0) {
      this.editor.chain().focus().setImage({ src: this.imgUrlInputTarget.value, alt: this.imgAltInputTarget.value, title: this.imgTitleInputTarget.value }).run()
    }
  }

  cleanImageInsert() {
    this.imgUrlInputTarget.value = ''
    this.imgAltInputTarget.value = ''
    this.imgTitleInputTarget.value = ''

    this.imagePickerContainerTarget.classList.remove('hidden')
    this.imageDetailContainerTarget.classList.add('hidden')
    this.imageSubmitButtonTarget.classList.add('hidden')
    this.imgPreviewTarget.src = ''
  }
}
